import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ChangeEvent } from 'react';

import { DisplayToast } from '@/helpers';

import { IMAGE_SIZE_LIMIT_B, IMAGE_SIZE_LIMIT_MB } from '@/constants';

type UseImagePickerProps = {
  url?: string;
  hasSizeLimit?: boolean;
  onChange: (value: File | '') => void;
};

export const useImagePicker = (props: UseImagePickerProps) => {
  const { url, hasSizeLimit = true, onChange } = props;
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(url || null);

  const openPicker = () => {
    inputRef.current?.click();
  };

  const selectFile = (file: File) => {
    if (hasSizeLimit && file.size > IMAGE_SIZE_LIMIT_B) {
      DisplayToast.error(t('fileWeightMsg', { size: IMAGE_SIZE_LIMIT_MB }));
      return;
    }

    onChange(file);

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setPreviewUrl(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const removeFile = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    onChange('');
    setPreviewUrl(null);
  };

  const inputProps = {
    ref: inputRef,
    type: 'file',
    accept: 'image/*',
    className: 'hidden',
    onChange: (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      if (file) {
        selectFile(file);
      }
    },
  };

  return { previewUrl, openPicker, removeFile, inputProps };
};
